import { FC, useState } from 'react'
import {
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
  ListSubheader
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Form, Formik } from 'formik'
import CloseIcon from '@mui/icons-material/Close'
import theme from 'theme'

/* Utils */
import { Tenant } from '../../../../../models'
import { timeZoneList } from '../account-managment.constants'

/* Components */
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  FormField,
  CancelButton,
  SaveButton
} from './EditCompanyInfoDrawer.styles'

interface IIndustries {
  label: string
  value: string
}

interface Props {
  open: boolean
  onClose: () => void
  form: Tenant
  industries: IIndustries[]
  onSubmit: (values: Tenant) => void
}

const dropdownMenuProps = {
  sx: {
    mt: '8px'
  },
  PaperProps: {
    sx: {
      background: theme.colors.menuBg,
      borderRadius: '12px',
      boxShadow: '0px 4px 8px rgba(30, 26, 29, 0.2)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      "html[data-theme='light'] &": {
        background: theme.colors.surfaceContainerBackground
      },
      '& .MuiList-root': {
        padding: 0,
        maxHeight: 340,
        overflowY: 'auto',
        marginRight: '4px',
        '&::-webkit-scrollbar': {
          width: '4px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#292128',
          borderRadius: '2px',
          "html[data-theme='light'] &": {
            backgroundColor: theme.colors.borderMedium
          }
        },
        '& .MuiListSubheader-root': {
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: theme.colors.menuBg,
          "html[data-theme='light'] &": {
            background: theme.colors.surfaceContainerBackground
          }
        }
      },
      '& .MuiMenuItem-root': {
        fontFamily: 'Quicksand, sans-serif',
        fontSize: '14px',
        fontWeight: 500,
        color: theme.colors.menuItemText,
        lineHeight: '22px',
        padding: '10px 16px',
        '&:hover': {
          background: 'rgba(255, 169, 252, 0.08)',
          "html[data-theme='light'] &": {
            background: theme.colors.menuItemHover
          }
        },
        '&.Mui-selected': {
          background: 'rgba(255, 169, 252, 0.12)',
          "html[data-theme='light'] &": {
            background: theme.colors.menuItemHover
          },
          '&:hover': {
            background: 'rgba(255, 169, 252, 0.16)',
            "html[data-theme='light'] &": {
              background: theme.colors.menuItemHover
            }
          }
        }
      }
    }
  }
}

const searchSubheaderSx = {
  p: '6px 8px',
  background: theme.colors.menuBg,
  lineHeight: 'unset',
  "html[data-theme='light'] &": {
    background: theme.colors.surfaceContainerBackground
  },
  '& .MuiOutlinedInput-root': {
    height: 'auto',
    padding: '8px 16px !important',
    background: '#292128',
    border: '1px solid #292128',
    borderRadius: '8px',
    gap: '12px',
    "html[data-theme='light'] &": {
      background: theme.colors.menuItemHover,
      borderColor: theme.colors.dropdownBorder
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiOutlinedInput-input': {
      color: theme.colors.devicesSecondaryText,
      fontFamily: 'Quicksand, sans-serif',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '0.1px',
      padding: '0 !important',
      '&::placeholder': {
        color: theme.colors.devicesSecondaryText,
        opacity: 1
      }
    },
    '& .MuiInputAdornment-root': {
      marginRight: 0
    }
  }
}

const EditCompanyInfoDrawer: FC<Props> = ({ open, onClose, form, industries, onSubmit }) => {
  const [industrySearch, setIndustrySearch] = useState('')
  const [timezoneSearch, setTimezoneSearch] = useState('')

  const timezoneOptions =
    timeZoneList.length > 0
      ? timeZoneList.map((item) => {
          const gmtMatch = item.name.match(/\(GMT[^)]*\)/)
          const gmtPart = gmtMatch ? ` ${gmtMatch[0]}` : ''
          return {
            label: `${item.timezone}${gmtPart}`,
            value: item.name
          }
        })
      : []

  const filteredIndustries = industrySearch
    ? industries.filter((i) => i.label.toLowerCase().includes(industrySearch.toLowerCase()))
    : industries

  const filteredTimezones = timezoneSearch
    ? timezoneOptions.filter((t) => t.label.toLowerCase().includes(timezoneSearch.toLowerCase()))
    : timezoneOptions

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <DrawerContent>
        <Formik initialValues={form} onSubmit={onSubmit} enableReinitialize>
          {({ values, handleChange, handleSubmit, isSubmitting, dirty, setFieldValue }) => (
            <Form
              autoComplete="off"
              style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
              onSubmit={handleSubmit}
            >
              <DrawerHeader>
                <Typography className="drawer-title">Edit Company Information</Typography>
                <IconButton className="close-button" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </DrawerHeader>

              <DrawerBody>
                <FormField>
                  <TextField
                    fullWidth
                    name="name"
                    label="Company Name"
                    value={values.name || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </FormField>

                <FormField>
                  <FormControl fullWidth>
                    <InputLabel shrink>Industry</InputLabel>
                    <Select
                      value={values.industry || ''}
                      onChange={(e) => {
                        setFieldValue('industry', e.target.value)
                      }}
                      onOpen={() => setIndustrySearch('')}
                      onClose={() => setIndustrySearch('')}
                      displayEmpty
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={dropdownMenuProps}
                      renderValue={(selected) => {
                        if (!selected) return <span style={{ color: theme.colors.devicesSecondaryText }}>Select industry</span>
                        const found = industries.find((i) => i.value === selected)
                        return found?.label || selected
                      }}
                    >
                      <ListSubheader sx={searchSubheaderSx}>
                        <TextField
                          fullWidth
                          autoFocus
                          autoComplete="off"
                          placeholder="Search"
                          value={industrySearch}
                          onChange={(e) => setIndustrySearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: theme.colors.dropdownLabel, fontSize: 20 }} />
                              </InputAdornment>
                            )
                          }}
                        />
                      </ListSubheader>
                      {filteredIndustries.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormField>

                <FormField>
                  <FormControl fullWidth>
                    <InputLabel shrink>Timezone</InputLabel>
                    <Select
                      value={values.timeZone || ''}
                      onChange={(e) => {
                        setFieldValue('timeZone', e.target.value)
                      }}
                      onOpen={() => setTimezoneSearch('')}
                      onClose={() => setTimezoneSearch('')}
                      displayEmpty
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={dropdownMenuProps}
                      renderValue={(selected) => {
                        if (!selected) return <span style={{ color: theme.colors.devicesSecondaryText }}>Select timezone</span>
                        const found = timezoneOptions.find((t) => t.value === selected)
                        return found?.label || selected
                      }}
                    >
                      <ListSubheader sx={searchSubheaderSx}>
                        <TextField
                          fullWidth
                          autoFocus
                          autoComplete="off"
                          placeholder="Search"
                          value={timezoneSearch}
                          onChange={(e) => setTimezoneSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: theme.colors.dropdownLabel, fontSize: 20 }} />
                              </InputAdornment>
                            )
                          }}
                        />
                      </ListSubheader>
                      {filteredTimezones.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormField>
              </DrawerBody>

              <DrawerFooter>
                <CancelButton onClick={onClose}>
                  <Typography className="button-text">Cancel</Typography>
                </CancelButton>
                <SaveButton type="submit" disabled={isSubmitting || !dirty}>
                  <Typography className="button-text">Save</Typography>
                </SaveButton>
              </DrawerFooter>
            </Form>
          )}
        </Formik>
      </DrawerContent>
    </Drawer>
  )
}

export default EditCompanyInfoDrawer
